/**
 * timeZoneToolkit.js
 *
 * Platform-level timezone helpers (Intl-powered).
 * Domain logic must not call Intl directly so it remains deterministic and
 * easy to unit test.
 */

/**
 * Formats an ISO instant into a YYYY-MM-DD date in a given IANA time zone.
 *
 * Why this exists:
 * - Date-only recurrence (`*_on`) needs a timezone to define day boundaries.
 * - Intl is the most portable way to map instants to a calendar date in a zone.
 *
 * @param {string} isoInstant
 * @param {string} tzid - IANA timezone identifier, e.g. "America/New_York"
 * @returns {string|null}
 */
export const toLocalISODateInTimeZone = (isoInstant, tzid) => {
  if (!isoInstant || !tzid) return null;

  const date = new Date(isoInstant);
  if (Number.isNaN(date.getTime())) return null;

  let parts;
  try {
    parts = new Intl.DateTimeFormat('en', {
      timeZone: tzid,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);
  } catch {
    // Invalid tzid or unsupported Intl implementation.
    return null;
  }

  const lookup = parts.reduce((acc, p) => {
    if (p.type === 'year' || p.type === 'month' || p.type === 'day') {
      acc[p.type] = p.value;
    }
    return acc;
  }, {});

  if (!lookup.year || !lookup.month || !lookup.day) return null;
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
};
